import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { FieldProps, FastFieldProps } from 'formik';
import { useTranslation } from 'react-i18next';

import MenuItem from '@material-ui/core/MenuItem';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

import withTooltip from 'components/Tooltip/withInfo';
import Info from 'components/Tooltip/Info';

import { withField } from './withFields';
import { getErrorMessageKey } from 'helpers/helpers';
import { useValueStyles } from './styles';

interface Options {
  value: string;
  label: string;
  tooltip?: string;
}

interface OwnProps {
  name: string;
  beforeChange?: ()=>void;
  afterChange?: () => void;
  specificError?: boolean;
  endAdornment?: string;
  readOnly?: boolean;
  options?: Options[];
  empty?: boolean;
}

type Props = OwnProps & TextFieldProps & (FieldProps | FastFieldProps);

/**
 * Basic wrapper around Mui TextField. Main purpose of this component is to set certain repeating
 * things like variant and to be again to be wrapped in formik's field which inject more props
 * like form and field to it that handle changes, submits and errors
 * You can pass options prop or classical MenuItem children components. Only up to you.
 *
 * @param name {string} - id and name for the input and name of formik value
 * @param onChangeSubmit {boolean} - optional parameter to submit the whole form after change.
 * @param specificError {boolean} - optional parameter that tell the component to display specific error from backend instead of generic one
 * @param TextFieldProps {any} - any material-ui TextField props can be applied
 * @param formikProps {any} - formik props so Formik can do it's things, they are added automaticaly via formiks Field
 */
const CustomFormSelectField: React.FunctionComponent<Props> = ({
  beforeChange,
  afterChange,
  specificError,
  field, // from formik
  form, // from formik
  meta, // from formik
  InputProps,
  endAdornment,
  readOnly,
  options,
  children,
  empty,
  ...rest
}) => {
  const { t } = useTranslation();
  const classes = useValueStyles();
  const [callAfterChange, setCallAfterChange] = useState(false);

  const inputProps: typeof InputProps = {
    readOnly,
    endAdornment: endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>,
    ...InputProps,
  };

  // If there is afterChange callback
  // and the field value changed, call it.
  useEffect(() => {
    if (callAfterChange) {
      afterChange?.();
      setCallAfterChange(false);
    }
  }, [field.value, callAfterChange, afterChange]);

  const errorMessage = meta.error ? t(getErrorMessageKey(meta.error)) : '';

  // when the value is changed and we want to show intialValues show them
  const note =
    form.status?.showInitialValues && meta.initialValue !== field.value
      ? `Původní hodnota: ${meta.initialValue || 'nevybráno'}`
      : '';

  const onChange = useCallback(
    (e: React.FocusEvent<any>) => {
      // If there is afterChange callback, set the flag
      // which informs the component to call the callback after
      // it do it's own onBlur and rerender with new props
      if (beforeChange) {
        beforeChange?.();
      }
      if (afterChange) {
        setCallAfterChange(true);
      }
      field.onChange(e);
    },
    [beforeChange, afterChange, field],
  );

  useEffect(() => {
    if (options && options.length === 1 && !field.value && !empty) {
      form.setFieldValue(field.name, options[0].value);
    }
  }, [empty, field.name, field.value, form, options]);

  const menuItems = useMemo(
    () =>
      options
        ? options.map(({ value, label, tooltip }) => (
            <MenuItem key={value} value={value} >
              {label}{tooltip &&<Info content={tooltip} button />}
            </MenuItem>
          ))
        : children,
    [children, options],
  );

  return (
    <TextField
      select={true}
      InputProps={inputProps}
      {...field}
      onChange={onChange}
      error={!!errorMessage}
      helperText={errorMessage || note}
      classes={{ root: readOnly ? classes.readOnly : '' }}
      {...rest}
    >
      {menuItems}
    </TextField>
  );
};

const FormSelectField = withField(withTooltip(CustomFormSelectField));

export default FormSelectField;
